/**
 * Utility functions for creating custom marker icons with activity count badges
 */

const BRAND_COLOR = '#8B1538';
const ICON_SIZE = 40;
const BADGE_SIZE = 18;
const FONT_SIZE = 9;
const FONT_FAMILY = 'Noto Sans Hebrew, Arial, sans-serif';

/**
 * Creates a custom marker icon with activity count displayed in a badge
 * @param activityCount - The number of activities for the contact
 * @returns Google Maps icon object with custom SVG
 */
export const createMarkerIcon = (activityCount: number): google.maps.Icon => {
  const count = activityCount || 0;
  const countText = count > 999 ? '999+' : count.toString();

  // Calculate badge size based on text length
  const textWidth = countText.length * (FONT_SIZE * 0.6);
  const badgeWidth = Math.max(BADGE_SIZE, textWidth + 10);

  // Add padding at the top to prevent clipping
  const TOP_PADDING = 4;
  const SVG_HEIGHT = ICON_SIZE + TOP_PADDING;

  // Create SVG for the marker icon with viewBox to prevent clipping
  const svg = `
    <svg width="${ICON_SIZE}" height="${SVG_HEIGHT}" viewBox="0 0 ${ICON_SIZE} ${SVG_HEIGHT}" xmlns="http://www.w3.org/2000/svg">
      <!-- Pin shadow -->
      <ellipse cx="${ICON_SIZE / 2}" cy="${ICON_SIZE - 2 + TOP_PADDING}" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
      
      <!-- Pin body -->
      <path d="M ${ICON_SIZE / 2} ${4 + TOP_PADDING} 
              Q ${ICON_SIZE / 2} ${4 + TOP_PADDING} ${ICON_SIZE / 2 + 8} ${12 + TOP_PADDING}
              Q ${ICON_SIZE / 2 + 8} ${12 + TOP_PADDING} ${ICON_SIZE / 2 + 8} ${20 + TOP_PADDING}
              Q ${ICON_SIZE / 2 + 8} ${28 + TOP_PADDING} ${ICON_SIZE / 2} ${32 + TOP_PADDING}
              Q ${ICON_SIZE / 2 - 8} ${28 + TOP_PADDING} ${ICON_SIZE / 2 - 8} ${20 + TOP_PADDING}
              Q ${ICON_SIZE / 2 - 8} ${12 + TOP_PADDING} ${ICON_SIZE / 2} ${4 + TOP_PADDING} Z" 
            fill="${BRAND_COLOR}" 
            stroke="white" 
            stroke-width="2"/>
      
      <!-- Activity count badge -->
      <circle cx="${ICON_SIZE / 2 + 6}" cy="${10 + TOP_PADDING}" r="${
    badgeWidth / 2
  }" fill="white" stroke="${BRAND_COLOR}" stroke-width="1.5"/>
      <text x="${ICON_SIZE / 2 + 6}" 
            y="${10 + TOP_PADDING + FONT_SIZE / 3}" 
            text-anchor="middle" 
            font-family="${FONT_FAMILY}" 
            font-size="${FONT_SIZE}" 
            font-weight="bold" 
            fill="${BRAND_COLOR}">
        ${countText}
      </text>
    </svg>
  `;

  // Convert SVG to data URL (no cleanup needed)
  const encodedSvg = encodeURIComponent(svg);
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;

  return {
    url: dataUrl,
    scaledSize: new google.maps.Size(ICON_SIZE, SVG_HEIGHT),
    anchor: new google.maps.Point(ICON_SIZE / 2, SVG_HEIGHT),
    origin: new google.maps.Point(0, 0)
  };
};

/**
 * Creates a simpler circular badge icon (alternative style)
 * @param activityCount - The number of activities for the contact
 * @returns Google Maps icon object with custom SVG
 */
export const createBadgeMarkerIcon = (activityCount: number): google.maps.Icon => {
  const count = activityCount || 0;
  const countText = count > 999 ? '999+' : count.toString();

  // Calculate badge size based on text length
  const textWidth = countText.length * (FONT_SIZE * 0.6);
  const badgeSize = Math.max(BADGE_SIZE, textWidth + 12);

  // Create SVG for a circular badge marker
  const svg = `
    <svg width="${badgeSize}" height="${badgeSize}" xmlns="http://www.w3.org/2000/svg">
      <!-- Shadow -->
      <circle cx="${badgeSize / 2}" cy="${badgeSize / 2 + 1}" r="${badgeSize / 2 - 1}" fill="rgba(0,0,0,0.2)"/>
      
      <!-- Badge circle -->
      <circle cx="${badgeSize / 2}" 
              cy="${badgeSize / 2}" 
              r="${badgeSize / 2 - 2}" 
              fill="${BRAND_COLOR}" 
              stroke="white" 
              stroke-width="2"/>
      
      <!-- Activity count text -->
      <text x="${badgeSize / 2}" 
            y="${badgeSize / 2 + FONT_SIZE / 3}" 
            text-anchor="middle" 
            font-family="${FONT_FAMILY}" 
            font-size="${FONT_SIZE}" 
            font-weight="bold" 
            fill="white">
        ${countText}
      </text>
    </svg>
  `;

  // Convert SVG to data URL (no cleanup needed)
  const encodedSvg = encodeURIComponent(svg);
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;

  return {
    url: dataUrl,
    scaledSize: new google.maps.Size(badgeSize, badgeSize),
    anchor: new google.maps.Point(badgeSize / 2, badgeSize / 2),
    origin: new google.maps.Point(0, 0)
  };
};
